import { Badge, Icon, SheetBody, SheetHeader } from "@nexus/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ScriptSentToVendorCard } from "@/features/opportunities/components/scripts/ScriptSentToVendorCard/ScriptSentToVendorCard.js";
import { isSentToAnyPartner } from "@/features/marketplace/lib/sent-scripts-map";
import type { SentScriptMeta } from "@/features/marketplace/types/marketplace.types";
import type {
  PersonaBriefContentPatch,
  PersonaBriefDto,
} from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import { ScriptPlatformBadge } from "@/features/persona-opportunities/components/platform/ScriptPlatformBadge";
import {
  createPendingBriefSave,
  shouldRetainBriefSaveFailure,
} from "@/features/persona-opportunities/lib/pending-brief-save";
import { formatPersonaBriefForCopy } from "@/features/persona-opportunities/lib/persona-brief-clipboard";
import { scriptDocxLabels } from "@/features/persona-opportunities/lib/persona-script-docx";
import { formatScriptCreatedAt } from "@/features/persona-opportunities/lib/persona-script-meta";
import {
  useAiEditPersonaBriefs,
  usePatchPersonaBriefContent,
  useTogglePersonaBriefFavorite,
} from "@/lib/queries";

import { BriefsHistoryPanel } from "../../workspace/tabs/ScriptsTab/BriefsHistoryDrawer.js";
import {
  parsePersonaBriefAiCell,
  PERSONA_BRIEF_CREATOR_COUNT,
} from "../../workspace/tabs/ScriptsTab/PersonaBriefCard.js";
import { PersonaAiBriefEditorModal } from "../../workspace/tabs/ScriptsTab/PersonaAiBriefEditorModal.js";
import { PersonaBriefExpandedBody } from "../../workspace/tabs/ScriptsTab/PersonaBriefExpandedBody.js";
import { PERSONA_SCRIPT_CELL_FLASH_MS } from "../../workspace/tabs/ScriptsTab/PersonaScriptEditableCell.js";
import { PersonaScriptTitleEditor } from "../../workspace/tabs/ScriptsTab/PersonaScriptTitleEditor.js";
import { PersonaSendToVendorDialog } from "../../workspace/tabs/ScriptsTab/PersonaSendToVendorDialog/PersonaSendToVendorDialog.js";
import { usePersonaBriefClipboard } from "../../workspace/tabs/ScriptsTab/usePersonaBriefClipboard.js";

import {
  marketplaceOrderHref,
  useOpenMarketplaceOrder,
} from "./hub-order-link.js";
import { OpportunityHubDrawerToolbar } from "./OpportunityHubDrawerToolbar.js";

const TITLE_KEY = "title";

function hasSuccessfulBriefEditResult(
  result: unknown,
  briefId: string,
): boolean {
  if (!result || typeof result !== "object") return false;
  const results = (result as { results?: unknown }).results;
  if (!Array.isArray(results)) return false;
  return results.some((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const row = entry as { briefId?: unknown; status?: unknown };
    return row.briefId === briefId && row.status === "success";
  });
}

export interface OpportunityHubBriefDocumentProps {
  brief: PersonaBriefDto;
  setId: string;
  tenantId: string;
  canEdit: boolean;
  sentMeta: SentScriptMeta | null;
  opportunityAngle: string;
  sheetTitle: string;
  setName: string;
}

/** Hub drawer brief view, laid out like the script document with an inline version rail. */
export function OpportunityHubBriefDocument({
  brief,
  setId,
  tenantId,
  canEdit,
  sentMeta,
  opportunityAngle,
  sheetTitle,
  setName,
}: OpportunityHubBriefDocumentProps) {
  const { t, i18n } = useTranslation();
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const [flash, setFlash] = useState<{
    key: string;
    source: "manual" | "ai";
  } | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [aiEditKey, setAiEditKey] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const pendingSaveRef = useRef(createPendingBriefSave());
  const previousEditingKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const previous = previousEditingKeyRef.current;
    previousEditingKeyRef.current = editingKey;
    // Leaving the field drops the edit. A settled failure would otherwise
    // block Share until reload, with no field name in the toast.
    if (previous && previous !== editingKey) pendingSaveRef.current.clearFailure(previous);
  }, [editingKey]);
  const [aiEditOpen, setAiEditOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  /** `null` is the current version; older versions hide actions that would edit the live brief. */
  const [historyVersion, setHistoryVersion] = useState<number | null>(null);
  const isViewingCurrent = historyVersion == null;
  const patchBrief = usePatchPersonaBriefContent();
  const toggleFavorite = useTogglePersonaBriefFavorite();
  const aiEdit = useAiEditPersonaBriefs();
  const openOrder = useOpenMarketplaceOrder();
  const { linkCopied, isSharing, copied, share, copy } = usePersonaBriefClipboard(brief, {
    beforeShare: () => pendingSaveRef.current.wait(),
  });

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const flashCell = useCallback(
    (key: string, source: "manual" | "ai" = "manual") => {
      setFlash({ key, source });
      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(
        () => setFlash(null),
        PERSONA_SCRIPT_CELL_FLASH_MS,
      );
    },
    [],
  );

  const saveContent = useCallback(
    (key: string, patch: PersonaBriefContentPatch) => {
      setSavingKey(key);
      const job = patchBrief
        .mutateAsync({ setId, briefId: brief.id, tenantId, patch })
        .then(
          () => {
            flashCell(key);
            return true;
          },
          () => false,
        )
        .finally(() => {
          setSavingKey((current) => (current === key ? null : current));
        });
      pendingSaveRef.current.track(key, job, {
        retainFailure: shouldRetainBriefSaveFailure(key),
      });
      return job;
    },
    [brief.id, flashCell, patchBrief, setId, tenantId],
  );

  const title = brief.videoName.trim();
  const orderHref = sentMeta
    ? marketplaceOrderHref(sentMeta.orderId, sentMeta.orderScriptId)
    : undefined;
  const isAiEditing =
    aiEdit.isPending &&
    !aiEdit.variables?.cell &&
    (aiEdit.variables?.briefIds.includes(brief.id) ?? false);

  function openAiEditor() {
    setEditingKey(null);
    setAiEditKey(null);
    setAiEditOpen(true);
  }

  function applyCellAi(cellKey: string, instruction: string) {
    const cell = parsePersonaBriefAiCell(cellKey);
    if (!cell) return;
    aiEdit.mutate(
      {
        setId,
        briefIds: [brief.id],
        instruction,
        tenantId,
        idempotencyKey: crypto.randomUUID(),
        cell,
      },
      {
        onSuccess: (result) => {
          if (hasSuccessfulBriefEditResult(result, brief.id))
            flashCell(cellKey, "ai");
        },
        onSettled: () =>
          setAiEditKey((current) => (current === cellKey ? null : current)),
      },
    );
  }

  return (
    <>
      <SheetHeader className="shrink-0 gap-0 px-6 py-0">
        <OpportunityHubDrawerToolbar
          sheetTitle={sheetTitle}
          setName={setName}
          isFavorite={brief.isFavorite}
          onToggleFavorite={
            canEdit
              ? () =>
                  toggleFavorite.mutate({
                    setId,
                    briefId: brief.id,
                    tenantId,
                    isFavorite: !brief.isFavorite,
                  })
              : undefined
          }
          copied={copied}
          onCopy={() => void copy()}
          copyLabel={t(
            "personaOpportunities.scripts.briefs.copy",
            "Copy brief",
          )}
          exportDocument={{
            part: {
              kind: "brief",
              text: formatPersonaBriefForCopy(brief, t),
              labels: scriptDocxLabels(t),
            },
          }}
          linkCopied={linkCopied}
          isSharing={isSharing}
          onShare={() => void share()}
          isAiEditing={isAiEditing}
          onAiEdit={canEdit ? openAiEditor : undefined}
          sentMeta={sentMeta}
          onSendToVendor={() => setSendOpen(true)}
          orderHref={orderHref}
          onOpenOrder={openOrder}
          hideActions={!isViewingCurrent}
        />
      </SheetHeader>
      <SheetBody className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden px-0 py-0">
        {sentMeta ? (
          <div className="shrink-0 px-6 pt-6">
            <ScriptSentToVendorCard sentMeta={sentMeta} className="mt-0" />
          </div>
        ) : null}
        <BriefsHistoryPanel
          enabled
          setId={setId}
          tenantId={tenantId}
          brief={brief}
          canRestore={canEdit}
          surface="drawer"
          hideCurrentDuration
          selection={{ version: historyVersion, onChange: setHistoryVersion }}
          leading={(view, fieldsPreview) => {
            const compared = view.changes && view.preview ? view.preview : null;
            const snapshot =
              !view.isLatest && view.preview && !view.isShowingChanges
                ? view.preview
                : null;
            const shown = compared ?? snapshot ?? brief;
            const readOnlyHistory = compared != null || snapshot != null;
            const showDocument =
              view.isLatest || readOnlyHistory || view.isShowingChanges;
            const shownCreatedAt = formatScriptCreatedAt(
              shown.createdAt,
              i18n.language,
            );

            return (
              <>
                {showDocument ? (
                  <div className="flex flex-col gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {canEdit && !readOnlyHistory ? (
                        <PersonaScriptTitleEditor
                          title={shown.videoName}
                          canEdit
                          titleClassName="text-drawer-title"
                          editAria={t(
                            "personaOpportunities.scripts.briefs.editTitleAria",
                            "Edit brief title",
                          )}
                          isEditing={editingKey === TITLE_KEY}
                          isFlashing={flash?.key === TITLE_KEY}
                          isSaving={savingKey === TITLE_KEY}
                          onStartEdit={() => {
                            setAiEditKey(null);
                            setEditingKey(TITLE_KEY);
                          }}
                          onStopEdit={() =>
                            setEditingKey((current) =>
                              current === TITLE_KEY ? null : current,
                            )
                          }
                          onSave={(next) =>
                            saveContent(TITLE_KEY, {
                              target: "videoName",
                              value: next,
                            })
                          }
                        />
                      ) : (
                        <p className="text-drawer-title text-foreground">
                          {shown.videoName}
                        </p>
                      )}
                      <ScriptPlatformBadge format={brief.youtubeFormat} />
                      {isSentToAnyPartner(sentMeta) ? (
                        <Badge variant="status-sent" iconStart="check">
                          {t(
                            "personaOpportunities.scripts.sentToProductionBadge",
                            "Sent to production",
                          )}
                        </Badge>
                      ) : null}
                    </div>
                    <p className="flex flex-wrap items-center gap-x-2 text-caption tabular-nums text-muted-foreground">
                      <span>
                        {t("personaOpportunities.scripts.briefs.meta", {
                          defaultValue_one:
                            "{{hooks}} hook · {{creators}} creator · {{duration}}",
                          defaultValue_other:
                            "{{hooks}} hooks · {{creators}} creator · {{duration}}",
                          count: shown.hookPackage,
                          duration: shown.duration,
                          hooks: shown.hookPackage,
                          creators: PERSONA_BRIEF_CREATOR_COUNT,
                        })}
                      </span>
                      {shownCreatedAt ? (
                        <span className="inline-flex items-center gap-1">
                          <Icon
                            name="clock"
                            size="xs"
                            color="muted"
                            aria-hidden
                          />
                          {shownCreatedAt}
                        </span>
                      ) : null}
                    </p>
                  </div>
                ) : null}
                {showDocument ? (
                  <PersonaBriefExpandedBody
                    brief={shown}
                    canEdit={canEdit && !readOnlyHistory}
                    editingKey={
                      readOnlyHistory || editingKey === TITLE_KEY
                        ? null
                        : editingKey
                    }
                    savingKey={
                      readOnlyHistory || savingKey === TITLE_KEY
                        ? null
                        : savingKey
                    }
                    aiEditKey={readOnlyHistory ? null : aiEditKey}
                    aiBusyKey={
                      !readOnlyHistory &&
                      aiEdit.isPending &&
                      aiEdit.variables?.cell &&
                      aiEditKey
                        ? aiEditKey
                        : null
                    }
                    flashKey={
                      !readOnlyHistory && flash && flash.key !== TITLE_KEY
                        ? flash.key
                        : null
                    }
                    flashSource={flash?.source ?? "manual"}
                    onStartEdit={(key) => {
                      setAiEditKey(null);
                      setEditingKey(key);
                    }}
                    onStopEdit={(key) =>
                      setEditingKey((current) =>
                        current === key ? null : current,
                      )
                    }
                    onStartAi={(key) => {
                      setEditingKey(null);
                      setAiEditOpen(false);
                      setAiEditKey(key);
                    }}
                    onStopAi={() => setAiEditKey(null)}
                    onApplyAi={
                      canEdit && !readOnlyHistory ? applyCellAi : undefined
                    }
                    onPersist={saveContent}
                    onDiscardSave={(key) => pendingSaveRef.current.clearFailure(key)}
                    beforeShare={() => pendingSaveRef.current.wait()}
                    isAiEditing={!readOnlyHistory && isAiEditing}
                    actionsPlacement="header"
                    surface="drawer"
                    fieldsPreview={readOnlyHistory ? null : fieldsPreview}
                    changes={compared ? view.changes : null}
                  />
                ) : (
                  fieldsPreview
                )}
              </>
            );
          }}
        />
      </SheetBody>
      {aiEditOpen ? (
        <PersonaAiBriefEditorModal
          open
          videoName={brief.videoName}
          opportunityTitle={opportunityAngle || null}
          isSubmitting={aiEdit.isPending}
          onClose={() => setAiEditOpen(false)}
          onSubmit={(instruction, idempotencyKey) => {
            aiEdit.mutate(
              {
                setId,
                briefIds: [brief.id],
                instruction,
                tenantId,
                idempotencyKey,
              },
              { onSuccess: () => setAiEditOpen(false) },
            );
          }}
        />
      ) : null}
      {sendOpen ? (
        <PersonaSendToVendorDialog
          open
          onOpenChange={setSendOpen}
          briefIds={[brief.id]}
          briefTitlesById={{ [brief.id]: title }}
          sentScriptsMap={sentMeta ? { [brief.id]: sentMeta } : {}}
          tenantId={tenantId}
          opportunitySetId={setId}
        />
      ) : null}
    </>
  );
}
